import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import Popup from '@ecp/common/src/components/modal/bo/Popup';

import { useAlertDialog, useConfirmDialog } from '@bo-hooks/common/useModal';
import memberApi from '@bo-apis/member/memberApi';
import coMemberApi from '@bo-apis/co/member/coMemberApi';
import Grid from '@bo-components/common/grid/Grid';
import { getGridOptions, MBR_ERR_CODE, getMemberRegValidators } from '../options/MemberRegConst';
import { isCODomain } from '@bo-const/common/domain';

const MemberRegPopup = ({ open, onClose, siteNumber, updateMemberCnt, entryMeanCode, subsidiaryList }) => {
  const isCo = isCODomain();
  const gridOptions = useMemo(
    () => getGridOptions(siteNumber, entryMeanCode, isCo, subsidiaryList),
    [siteNumber, entryMeanCode, isCo, subsidiaryList]
  );
  const { showAlertDialog } = useAlertDialog();
  const { showConfirmDialog } = useConfirmDialog();
  const gridRef = useRef(null);
  const subsidiaryCodeList = useMemo(
    () => subsidiaryList.map(({ subsidiaryCompanyCode }) => subsidiaryCompanyCode),
    [subsidiaryList]
  );
  const handleFirstRender = () => {};

  const createMember = useCallback(
    async (memberList) => {
      const createMember = isCo ? coMemberApi.createCoSiteMemberList : memberApi.createMemberList;
      const { message, code } = await createMember({ memberList, siteNumber });
      if (message) {
        showAlertDialog(`${MBR_ERR_CODE[code]} [${message}]`);
        return;
      }
      updateMemberCnt?.();
      showAlertDialog(`저장되었습니다.`, ()=> onClose());
    },
    [onClose, showAlertDialog, siteNumber, updateMemberCnt, isCo]
  );

  const handleConfirm = useCallback(async () => {
    if (!(await gridRef.current.validate())) return;

    const gridRows = gridRef.current.getDataRows();
    if (gridRows?.length > 50_000) {
      showAlertDialog('최대 5만건 입력이 가능합니다.');
      return;
    }

    const keys = gridOptions.Cols.map(({ Name }) => Name);
    const memberList =
      gridRows?.map((row) => ({
        ...keys.reduce((prev, curr) => {
          if (curr === 'memberDivisionCode') {
            prev[curr] = '20';
          } else {
            prev[curr] = row[curr];
          }
          return prev;
        }, {}),
      })) || [];

    showConfirmDialog(`저장하시겠습니까? 입력: ${memberList.length}건`, () => createMember(memberList));
  }, [showConfirmDialog, showAlertDialog, createMember]);

  const closePopup = () => {
    const gridRows = gridRef.current.getDataRows();
    if (gridRows?.length > 0) {
      showConfirmDialog('입력중인 항목이 있습니다.\n저장없이 취소하시겠습니까?', onClose);
    } else {
      onClose();
    }
  };

  return (
    <Popup
      type={'Large'}
      open={open}
      header={'회원등록'}
      onConfirmClick={handleConfirm}
      onClose={closePopup}
      onCancelClick={closePopup}
      confirmButtonText={'저장'}
    >
      <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
        <Spacing top={'16px'} />
        {subsidiaryCodeList.length > 0 && (
          <Grid
            unPaged
            showDelete
            showAddRow
            title={'회원등록'}
            excel={'custom'}
            customExcelForm={[
              { key: 'template', label: '양식다운로드' },
              { key: 'upload', label: '엑셀업로드' },
            ]}
            data={[]}
            options={gridOptions}
            onRenderFirstFinish={handleFirstRender}
            height={'516px'}
            ref={gridRef}
            validators={getMemberRegValidators(siteNumber, subsidiaryCodeList, isCo)}
          />
        )}
      </FlexBoxColumn>
    </Popup>
  );
};
export default MemberRegPopup;

MemberRegPopup.propTypes = {
  siteNumber: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateMemberCnt: PropTypes.func,
  entryMeanCode: PropTypes.string,
  subsidiaryList: PropTypes.arrayOf(PropTypes.object),
};
